import {
  Box,
  Button,
  Divider,
  Grid,
  Typography,
  MenuItem
} from "@mui/material";
import ServerMessageSnackbar from "../../../reusableComponents/serverMessageSnackbar/serverMessageSnackbar";
import { useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../hooks/hooks";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { filterAutofillGuard } from "../../../../utils/tableUtils";
import { MRT_ColumnDef, useMaterialReactTable } from "material-react-table";

import TableTemplate from "../../../reusableComponents/tableTemplate/tableTemplate";

import {
  deleteTrainingUser,
  enableUser,
  getUsers,
} from "../../../../store/thunks/systemManagementThunk";

import {
  getTrainingUsers
} from "../../../../store/thunks/portalManagementThunk";

import {
  logoutUser,
  resetPasswordLink,
} from "../../../../store/thunks/authenticationThunk";

import {
  logout,
} from "../../../../store/features/authentication/authenticationSlice";

import {
  portalManagementReset,
} from "../../../../store/features/portalManagement/portalManagementSlice";

import {
  systemManagementReset,
} from "../../../../store/features/systemManagement/systemManagementSlice";

import { exportToExcel, userFullName, withUserName } from "../../../../utils/utils";

import CreateUserDialog from "./createUserDialog/createUserDialog";
import EditUserDialog from "./editUserDialog/editUserDialog";
import ConfirmUserActionDialog from "../../../reusableComponents/confirmUserActionDialog/confirmUserActionDialog";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import UnsubscribeIcon from "@mui/icons-material/Unsubscribe";
import LockResetIcon from "@mui/icons-material/LockReset";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

type UserFilter = "active" | "disabled";

const UserManagement = () => {
  const dispatch = useAppDispatch();

  const {
    trainingUsersLoading,
    trainingUsers,
    selectedClient
} = useAppSelector(s => s.portalManagement);
  const { user, resetPasswordLinkLoading } = useAppSelector((s: any) => s.authentication);

  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  const [dialogs, setDialogs] = useState({
    create: false,
    edit: null as any,
    disable: null as any,
    enable: null as any
  });

  // Removing someone from this screen revokes THIS client. Whether that ends
  // the account depends on whether it was their last one, and the confirmation
  // should say which before it is accepted — hence client_count from the API.
  const revoking = dialogs.disable;
  const isLastClient = (revoking?.client_count ?? 1) <= 1;

  // Fetch users
  const { isLoading, refetch } = useQuery<any>({
    queryKey: ["users"],
    queryFn: async () => {
      const result: any = await dispatch(getTrainingUsers({}));
      if (result?.error?.message === "Rejected") {
        dispatch(logoutUser({ user_index: user?.client_user_index }));
        dispatch(portalManagementReset());
        dispatch(systemManagementReset());
        dispatch(logout());
        return [];
      }
      return trainingUsers || [];
  },
    placeholderData: keepPreviousData,
  });

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorFn: (row) => `${row.first_name} ${row.last_name}`,
        id: "name",
        header: "Full Name",
        size: 220,
      },
      {
        accessorKey: "user_name",
        header: "Email",
        enableClickToCopy: true,
        size: 260,
      },
      { accessorKey: "role_type", header: "Role", size: 160 },
      { accessorKey: "r_operand",
        header: "School / Domain",
        size: 160
      }
    ],
    []
  );

  const table = useMaterialReactTable({
    muiFilterTextFieldProps: filterAutofillGuard,
    columns,
    data: trainingUsers || [],
    state: { isLoading: isLoading || trainingUsersLoading || resetPasswordLinkLoading },
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableRowActions: true,
    positionToolbarAlertBanner: "bottom",
    initialState: {
      showColumnFilters: true,
      pagination: { pageIndex: 0, pageSize: 10 },
    },
    muiSearchTextFieldProps: { size: "small", variant: "outlined" },
    renderTopToolbarCustomActions: ({ table }) => (
      <Button
        disabled={!table.getPrePaginationRowModel().rows.length}
        startIcon={<FileDownloadIcon />}
        onClick={() => exportToExcel(table.getPrePaginationRowModel().rows, "Zintoro Users")}
      >
        Export All
      </Button>
    ),
    renderRowActionMenuItems: ({ row, closeMenu }) => {
      return [ 
        <MenuItem
            key="edit"
            onClick={() => {
                setDialogs((d) => ({ ...d, edit: row.original }));
                closeMenu();
            }}
            >
            <EditIcon /> Edit User
        </MenuItem>,

        <MenuItem
            key="disable"
            onClick={() => {
                setDialogs((d) => ({ ...d, disable: row.original }));
                closeMenu();
            }}
            >
            <DeleteIcon /> Delete User
        </MenuItem>,

    //     // <MenuItem
    //     //     key="enable"
    //     //     onClick={() => {
    //     //         setDialogs((d) => ({ ...d, enable: row.original }));
    //     //         closeMenu();
    //     //     }}
    //     //     >
    //     //     <EditIcon /> Enable User
    //     // </MenuItem>

    //     // <MenuItem
    //     //     key="reset"
    //     //     onClick={() => {
    //     //         dispatch(resetPasswordLink({ username: row.original.user_name })).then((res: any) => {
    //     //         setSnackbar({ open: true, message: res.payload?.message || "Password reset link sent" });
    //     //         });
    //     //         closeMenu();
    //     //     }}
    //     //     >
    //     //     <LockResetIcon /> Reset Password
    //     // </MenuItem>

      ].filter(Boolean);
    },
  });

  const handleDialogClose = (type: keyof typeof dialogs, message?: string) => {
    // Read the row before clearing it — the message names whoever was acted on.
    const name = userFullName(dialogs[type]);
    setDialogs((prev) => ({ ...prev, [type]: null }));
    if (message) {
      setSnackbar({ open: true, message: withUserName(message, name) });
      refetch();
    }
  };

  return (
    <Grid>
      <Grid
        container
        item
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 20px 10px 20px",
        }}
      >
        <Typography variant="h4" fontWeight="bold" color="#004680">
          User Management
        </Typography>
      </Grid>

      <Grid
            container
            item
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0px 20px 10px 20px !important",
            }}
          >
            <Grid sx={{ justifyContent: "center", padding: "5px" }}>
            <Button
              sx={{
                  color: "white !important",
                  fontWeight: "bold !important",
                  padding: "0px 20px 0px 20px !important",
                  backgroundColor: "#38B54A !important",
                }}
              variant="outlined"
              color="success"
              onClick={() => setDialogs((d) => ({ ...d, create: true }))}
            >
              Add User
            </Button>
          </Grid>
        
      </Grid>

      <Grid
        container
          item
          xs={12}
          sx={{
            display: "flex",
            padding: "0px 20px 20px 20px",
          }}
        >
        {/* <Grid
          sx={{
              display: "flex",
              flexDirection: "row",
              backgroundColor: "#4C4C4C !important",
              borderTop: "5px solid white !important",
              height: "40px",
              paddingLeft: "10px",
            }}
        >
            <Button
              sx={{
                color:
                  filter === "active"
                    ? "#38B54A !important"
                    : "white !important",
                padding: "0px 20px 0px 20px !important",
              }}
              onClick={() => setFilter("active")}
            >
              Active Users
            </Button>
            <Divider orientation="vertical" variant="middle" flexItem sx={{ bgcolor: "white" }} />
            <Button
              sx={{
                color:
                  filter === "disabled"
                    ? "#38B54A !important"
                    : "white !important",
                padding: "0px 20px 0px 20px !important",
              }}
              onClick={() => setFilter("disabled")}
            >
              Disabled Users
            </Button>
        </Grid> */}
      </Grid> 
      
      <Grid container item xs={12} sx={{ padding: "10px 20px 10px 20px" }}>
        <TableTemplate tableData={table} />
      </Grid>

      {/* Dialogs */}
      <CreateUserDialog
        open={dialogs.create}
        handleCreateClose={(msg?: string) => handleDialogClose("create", msg)} 
      />
      <EditUserDialog
        open={!!dialogs.edit}
        editUserData={dialogs.edit}
        handleEditClose={(msg?: string) => handleDialogClose("edit", msg)}
      />
      <ConfirmUserActionDialog
        open={!!dialogs.disable}
        title={isLastClient ? "Delete User" : "Remove Access"}
        verb={isLastClient ? "delete this user" : "remove access"}
        user={dialogs.disable}
        onConfirm={() =>
          dispatch(
            deleteTrainingUser({
              client_user_index: revoking?.clients_user_id,
              user_id: revoking?.id,
              client_index: selectedClient?.client_index,
            })
          )
        }
        onClose={(msg: string) => handleDialogClose("disable", msg)}
      />
      <ConfirmUserActionDialog
        open={!!dialogs.enable}
        title="Enable User"
        verb="enable"
        user={dialogs.enable}
        onConfirm={() =>
          dispatch(enableUser({ client_user_index: dialogs.enable?.client_user_index }))
        }
        onClose={(msg: string) => handleDialogClose("enable", msg)}
      />

      <ServerMessageSnackbar
        open={snackbar.open}
        message={snackbar.message}
        onClose={() => setSnackbar({ open: false, message: "" })}
      />

    </Grid>
  );
};

export default UserManagement;